import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatDateHuman } from '@/lib/utils'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.priceAlerts

/** Kitchen flow lane D: receipts whose price rose past the threshold. */
export function PriceAlertsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { formatCurrency } = useCurrency()
  const { data, isFetching: isLoading } = useQuery({
    queryKey: ['priceAlerts'],
    queryFn: () => apiClient.getPriceAlerts(30).then((r) => r.data),
    enabled: open,
    // Always fetch fresh when shown: the app-wide 5-minute staleTime would
    // otherwise show a list from before the last receipt / count / issue.
    staleTime: 0,
  })
  const alerts = data?.alerts ?? []
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{S.title}</DialogTitle>
          <DialogDescription>{S.help(data?.threshold_pct ?? 10)}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="py-6 text-sm text-muted-foreground">Loading…</p>
        ) : alerts.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{S.empty}</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((a, i) => (
              <li key={`${a.stock_item_id}-${a.received_at}-${i}`} className="rounded-lg border p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">{a.name}</span>
                  <span className="text-sm font-bold text-danger-fg tabular-nums">▲ {a.change_pct}%</span>
                </div>
                <p className="text-sm tabular-nums">
                  {S.was} {formatCurrency(a.previous_cost)} → {S.now} <span className="font-semibold">{formatCurrency(a.unit_cost)}</span> / {a.unit}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDateHuman(a.received_at)}{a.supplier_name ? ` · ${a.supplier_name}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
